import React, { useState } from 'react';
import './Contact.css';
import { useTranslation } from '../../i18n/TranslationContext';

const Contact: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    address: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{success: boolean, message: string} | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      console.log('Form data:', formData); // Debug log
      
      // Create payload for leadsender using the new flexible fields format
      const payload = {
        email: formData.email,
        phone: formData.phone || 'Not provided',
        sourceDomain: window.location.hostname, // Dynamic domain detection
        fields: {
          'Full Name': formData.name,
          'Service Requested': formData.service || 'Not specified',
          'Message': formData.message,
          'Address': formData.address || 'Not provided'
        }
      };
      
      console.log('Sending payload:', payload); // Debug log
      
      // Send to LeadSender API - using the absolute URL to ensure it reaches our service
      const response = await fetch('https://leads.nodexweb.live/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (response.ok) {
        setSubmitStatus({
          success: true,
          message: t.contact.form.success
        });
        
        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
          address: ''
        });
      } else {
        console.error('Error response:', data);
        setSubmitStatus({
          success: false,
          message: t.contact.form.error
        });
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus({
        success: false,
        message: t.contact.form.error
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact-section">
      <div className="container">
        <h2 className="section-title">{t.contact.title}</h2>
        <div className="contact-container">
          <div className="contact-info glass-panel">
            <h3>{t.contact.info.title}</h3>
            <p>{t.contact.subtitle}</p>
            
            <ul className="contact-details">
              <li>
                <i className="fas fa-map-marker-alt"></i>
                <div>
                  <h4>{t.contact.info.address.label}</h4>
                  <span>{t.contact.info.address.value}</span>
                </div>
              </li>
              <li>
                <i className="fas fa-phone"></i>
                <div>
                  <h4>{t.contact.info.phone.label}</h4>
                  <a href="tel:4189079922">{t.contact.info.phone.value}</a>
                </div>
              </li>
              <li>
                <i className="fas fa-envelope"></i>
                <div>
                  <h4>{t.contact.info.email.label}</h4>
                  <a href="mailto:vaudreuilmultiservices@gmail.com">{t.contact.info.email.value}</a>
                </div>
              </li>
              <li>
                <i className="fas fa-clock"></i>
                <div>
                  <h4>{t.contact.info.hours.label}</h4>
                  <span>{t.contact.info.hours.weekdays}</span>
                  <span>{t.contact.info.hours.saturday}</span>
                  <span>{t.contact.info.hours.sunday}</span>
                </div>
              </li>
            </ul>
            
            <div className="social-links">
              <a href="https://www.facebook.com/vaudreuilmultiservicesinc/" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><i className="fab fa-facebook-f"></i></a>
            </div>
          </div>
          
          <div className="contact-form-container glass-panel">
            <h3>{t.contact.form.title}</h3>
            {submitStatus && (
              <div className={`form-message ${submitStatus.success ? 'success' : 'error'}`}>
                {submitStatus.message}
              </div>
            )}
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={t.contact.form.name.placeholder}
                  required
                />
                <label htmlFor="name">{t.contact.form.name.label}</label>
              </div>
              
              <div className="form-group">
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t.contact.form.email.placeholder}
                  required
                />
                <label htmlFor="email">{t.contact.form.email.label}</label>
              </div>
              
              <div className="form-group">
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder={t.contact.form.phone.placeholder}
                />
                <label htmlFor="phone">{t.contact.form.phone.label} {t.contact.form.phone.optional}</label>
              </div>
              
              <div className="form-group">
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder={t.contact.form.address?.placeholder || 'Your address'}
                />
                <label htmlFor="address">{t.contact.info.address.label} {t.contact.form.address?.optional || '(optional)'}</label>
              </div>
              
              <div className="form-group">
                <label htmlFor="service">{t.services.title}</label>
                <div className="select-wrapper">
                  <select 
                    id="service" 
                    name="service" 
                    value={formData.service}
                    onChange={handleChange}
                  >
                    <option value="">{t.contact.form.service?.placeholder || 'Select a service'}</option>
                    <option value="excavation">{t.services.items.excavation.title}</option>
                    <option value="landscaping">{t.services.items.landscaping.title}</option>
                    <option value="leveling">{t.services.items.leveling.title}</option>
                    <option value="snowRemoval">{t.services.items.snowRemoval.title}</option>
                    <option value="drainage">{t.services.items.drainage.title}</option>
                    <option value="concrete">{t.services.items.installation.title}</option>
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <textarea 
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={t.contact.form.message.placeholder}
                  required
                  rows={5}
                />
                <label htmlFor="message">{t.contact.form.message.label}</label>
              </div>
              
              <button 
                type="submit" 
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? t.contact.form.submitting : t.contact.form.submit}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;